import React, { memo, useMemo } from 'react'
import type { CustomCellEditorProps } from 'ag-grid-react'
import { formatValueByType } from '../../../../../utils/helpers'
import { VariableType } from '../../../../../components/Table/types'
import { extractDataValue, isCellModified } from '../../../../../shared/utils/balanceRegistry'
import NumericCellEditor from '../../../../../shared/components/custom/Grid/Editor/NumericCellEditor'
import PercentageCellEditor from '../../../../../shared/components/custom/Grid/Editor/PercentageCellEditor'
import { DualValueCell } from '../cellRenderers/DualValueCell'

/**
 * Dual Value Cell Editor
 *
 * This editor is used when displaying both calculated and input values.
 * Shows calculated value (read-only) on top and editable input value below.
 */
const DualValueCellEditor = memo((props: CustomCellEditorProps<any, number>) => {
  const { column, data } = props

  const context = (column?.getColDef() as any)?.context
  const columnData = context.column

  // Check if this row uses reference budgeting method
  const isReferenceMethod = data?.budgetingMethod?.method === 'reference'

  // Get calculated value (from budget - calculated)
  const calculatedValue = extractDataValue(data, columnData, 'budget')

  // Input value comes from valueGetter (budgets/newBudgets)
  const inputValue = props.value

  // Format calculated value (always absolute - it's the computed result)
  const formattedCalculatedValue = useMemo(() => {
    if (calculatedValue === undefined || calculatedValue === null) return ''
    return formatValueByType(calculatedValue, VariableType.absolute)
  }, [calculatedValue])

  // Check if value is edited
  const isEdited = columnData ? isCellModified(data, columnData, 'newBudgets') : false

  // Select appropriate editor based on budgeting method
  const EditorComponent = isReferenceMethod ? PercentageCellEditor : NumericCellEditor

  // DualValueCellEditor is now only used when showing both calculated and input values
  return (
    <DualValueCell
      calculatedContent={<div style={{ marginRight: 4 }}>{formattedCalculatedValue}</div>}
      inputContent={<EditorComponent {...props} value={inputValue} />}
      style={{ justifyContent: 'flex-end' }}
      isEdited={isEdited}
    />
  )
})

DualValueCellEditor.displayName = 'DualValueCellEditor'

export default DualValueCellEditor
